import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { bookAppointment, listSlots, uploadAppointmentReport } from "@/api/appointmentApi";
import { getDoctorByKeycloakId } from "@/api/userApi";
import {
  ymdAddDaysInAppointmentZone,
  ymdTodayInAppointmentZone,
} from "@/lib/appointmentCalendar";
import {
  browserMatchesClinicZone,
  browserTimeZone,
  clinicTimeZoneOf,
  clinicZoneShortLabel,
  formatInstantInBrowserZone,
  formatInstantInClinicZone,
  instantFromClinicWall,
} from "@/lib/clinicTime";
import { PageHeader } from "@/components/common/PageHeader";

const QUICK_DATE_COUNT = 5;

function quickTabLabel(iso: string): string {
  const z = ymdTodayInAppointmentZone();
  const noon = instantFromClinicWall(iso, "12:00:00");
  if (iso === z) {
    return `Today · ${formatInstantInBrowserZone(noon, "EEE d MMM")}`;
  }
  if (iso === ymdAddDaysInAppointmentZone(z, 1)) {
    return `Tomorrow · ${formatInstantInBrowserZone(noon, "EEE d MMM")}`;
  }
  return formatInstantInBrowserZone(noon, "EEE d MMM");
}

function slotsLoadErrorMessage(err: unknown): string {
  if (!isAxiosError(err)) {
    return "Could not load slots. Try again.";
  }
  const data = err.response?.data;
  if (data && typeof data === "object" && "detail" in data) {
    const d = (data as { detail?: unknown }).detail;
    if (typeof d === "string" && d.trim()) return d;
  }
  if (err.response?.status === 502) {
    return "Could not load the doctor's schedule. Try again in a moment.";
  }
  return "Could not load slots. Try again.";
}

export default function BookAppointmentPage() {
  const { doctorKeycloakId = "" } = useParams();
  const navigate = useNavigate();
  const todayYmd = ymdTodayInAppointmentZone();
  const quickDates = useMemo(
    () =>
      Array.from({ length: QUICK_DATE_COUNT }, (_, i) =>
        ymdAddDaysInAppointmentZone(todayYmd, i),
      ),
    [todayYmd],
  );
  const [date, setDate] = useState(() => ymdTodayInAppointmentZone());
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [pendingDoc, setPendingDoc] = useState<File | null>(null);
  const [docAttachmentNote, setDocAttachmentNote] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadSnackbar, setUploadSnackbar] = useState<string | null>(null);

  const tabValue = quickDates.includes(date) ? date : false;

  const { data: doctor } = useQuery({
    queryKey: ["doctor-by-kc", doctorKeycloakId],
    queryFn: () => getDoctorByKeycloakId(doctorKeycloakId),
    enabled: Boolean(doctorKeycloakId),
  });

  const {
    data: slots,
    isLoading: slotsLoading,
    isError,
    error: slotsError,
  } = useQuery({
    queryKey: ["slots", doctorKeycloakId, date],
    queryFn: () => listSlots(doctorKeycloakId, date),
    enabled: Boolean(doctorKeycloakId && date),
  });

  const book = useMutation({
    mutationFn: async () => {
      const appt = await bookAppointment({
        doctorKeycloakId,
        slotId: selected!,
        patientNote: note || undefined,
      });
      if (pendingDoc) {
        try {
          await uploadAppointmentReport(appt.id, pendingDoc, docAttachmentNote || undefined);
        } catch {
          return {
            appt,
            uploadMessage:
              "Booking saved, but the document could not be uploaded. Add it from appointment details.",
          };
        }
      }
      return { appt, uploadMessage: null as string | null };
    },
    onSuccess: ({ appt, uploadMessage }) => {
      if (uploadMessage) setUploadSnackbar(uploadMessage);
      navigate(`/patient/appointments/${appt.id}/pay`);
    },
  });

  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      <PageHeader
        title="Book appointment"
        subtitle="Pick a slot — payment is collected after booking."
      />

      {doctor && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={doctor.photoUrl}
              sx={{ width: 56, height: 56, bgcolor: "primary.main" }}
            >
              {doctor.fullName?.[0]}
            </Avatar>
            <Box>
              <Typography fontWeight={700}>{doctor.fullName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {doctor.specialization}
                {doctor.consultationFee != null &&
                  ` · ₹${doctor.consultationFee} / consult`}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Pick a date
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Booking calendar days and slot times are published in the
          clinic&apos;s zone ({clinicTimeZoneOf(slots?.[0]?.timeZone)} ·{" "}
          {clinicZoneShortLabel(slots?.[0]?.timeZone)}). Each slot also shows
          the time in your browser zone ({browserTimeZone()}) so you know when
          to be online.
        </Typography>
        <Tabs
          value={tabValue}
          onChange={(_, iso: string) => {
            setDate(iso);
            setSelected(null);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            mb: 2,
            minHeight: 48,
            "& .MuiTab-root": { minHeight: 48, textTransform: "none" },
          }}
        >
          {quickDates.map((iso) => (
            <Tab
              key={iso}
              value={iso}
              label={quickTabLabel(iso)}
              aria-label={`Slots on ${iso}`}
            />
          ))}
        </Tabs>

        <TextField
          type="date"
          label="Or choose another date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setSelected(null);
          }}
          inputProps={{ min: ymdTodayInAppointmentZone() }}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ maxWidth: 280, mb: 2 }}
        />

        {slotsLoading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Alert severity="error">
            {slotsLoadErrorMessage(slotsError)}
          </Alert>
        )}
        {!slotsLoading && slots && slots.length === 0 && (
          <Alert severity="info">
            No slots available on this date. Try another day.
          </Alert>
        )}

        {slots && slots.length > 0 && (
          <Grid container spacing={1}>
            {slots.map((s) => {
              const taken = !s.available;
              const active = selected === s.id;
              // Prefer the UTC instant from the server; fall back to building
              // it from the clinic-local date + time using the server-supplied
              // zone so the browser never silently picks a wrong one.
              const when = s.startUtc
                ? new Date(s.startUtc)
                : instantFromClinicWall(s.date, s.startTime, s.timeZone);
              const clinicLabel = Number.isNaN(when.getTime())
                ? s.startTime
                : formatInstantInClinicZone(when, "h:mm a", s.timeZone);
              const browserLabel = Number.isNaN(when.getTime())
                ? ""
                : formatInstantInBrowserZone(when, "h:mm a");
              const showBoth =
                browserLabel !== clinicLabel &&
                !browserMatchesClinicZone(s.timeZone);
              return (
                <Grid item key={s.id}>
                  <Button
                    disabled={taken}
                    variant={active ? "contained" : "outlined"}
                    onClick={() => setSelected(s.id)}
                    sx={{
                      minWidth: 96,
                      opacity: taken ? 0.45 : 1,
                      display: "flex",
                      flexDirection: "column",
                      lineHeight: 1.2,
                      py: 0.75,
                    }}
                  >
                    <span>
                      {clinicLabel} {clinicZoneShortLabel(s.timeZone)}
                    </span>
                    {showBoth && (
                      <span style={{ fontSize: 10, opacity: 0.75 }}>
                        {browserLabel} your time
                      </span>
                    )}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        )}
        {slots && slots.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            Slot times are published in {clinicTimeZoneOf(slots[0]?.timeZone)}{" "}
            ({clinicZoneShortLabel(slots[0]?.timeZone)}). The smaller line
            on each slot shows the equivalent in your browser zone (
            {browserTimeZone()}).
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <TextField
          label="Note for doctor (optional)"
          multiline
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
          One document with this booking (optional)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Choose a single file. You can add up to 10 documents later from appointment details. PDF, images, or
          Word — up to 15 MB. Your doctor can open them after the appointment is confirmed.
        </Typography>
        <TextField
          label="Note for this file (optional)"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={docAttachmentNote}
          onChange={(e) => setDocAttachmentNote(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          sx={{ mb: 1.5 }}
        />
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,application/pdf,image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            setPendingDoc(f ?? null);
            e.target.value = "";
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose file
          </Button>
          {pendingDoc && (
            <Button size="small" onClick={() => setPendingDoc(null)}>
              Clear file
            </Button>
          )}
        </Stack>
        {pendingDoc && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selected: {pendingDoc.name}
          </Typography>
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={() => navigate(-1)}>Back</Button>
          <Button
            variant="contained"
            disabled={!selected || book.isPending}
            onClick={() => book.mutate()}
          >
            {book.isPending ? "Booking…" : "Confirm & pay"}
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(uploadSnackbar)}
        autoHideDuration={8000}
        onClose={() => setUploadSnackbar(null)}
        message={uploadSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
